import { useEffect, useState } from 'react'

interface BlockedShardNotification {
  id: string
  shardId: string
  title: string
  timestamp: number
}

interface BlockedShardToastProps {
  notifications: BlockedShardNotification[]
  onDismiss: (id: string) => void
}

export function BlockedShardToast({ notifications, onDismiss }: BlockedShardToastProps) {
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
      {notifications.map((notification) => (
        <ToastItem
          key={notification.id}
          notification={notification}
          onDismiss={() => onDismiss(notification.id)}
        />
      ))}
    </div>
  )
}

interface ToastItemProps {
  notification: BlockedShardNotification
  onDismiss: () => void
}

function ToastItem({ notification, onDismiss }: ToastItemProps) {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Trigger enter animation
    const showTimer = setTimeout(() => setIsVisible(true), 10)

    // Auto-dismiss after 5 seconds
    const dismissTimer = setTimeout(() => {
      setIsVisible(false)
      setTimeout(onDismiss, 300) // Wait for exit animation
    }, 5000)

    return () => {
      clearTimeout(showTimer)
      clearTimeout(dismissTimer)
    }
  }, [onDismiss])

  return (
    <div
      className={`
        themed-panel border border-cosmic-plasma/30 rounded-lg shadow-2xl
        backdrop-blur-md bg-cosmic-abyss/90
        transform transition-all duration-300 ease-out
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        {/* Animated warning indicator */}
        <div className="w-2 h-2 mt-1.5 bg-cosmic-plasma rounded-full animate-pulse" />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-cosmic-plasma font-medium text-sm">
            Shard Blocked
          </p>
          <p className="text-gray-300 text-sm mt-0.5 truncate">
            {notification.shardId} needs attention
          </p>
          {notification.title && (
            <p className="text-gray-500 text-xs mt-1 truncate">
              {notification.title}
            </p>
          )}
        </div>

        {/* Dismiss button */}
        <button
          onClick={() => {
            setIsVisible(false)
            setTimeout(onDismiss, 300)
          }}
          className="text-gray-400 hover:text-white transition-colors p-0.5"
          aria-label="Dismiss notification"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  )
}

export type { BlockedShardNotification }
